import { CalendarClock, ClipboardList, Contact, FileText, LayoutDashboard, MapPin, Truck, Users, Wheat, type LucideIcon } from 'lucide-react'
import type { MessageKey } from '../i18n/messages'

export interface NavItem {
  /** Rótulo em PT (fallback quando não há tradução para `labelKey`). */
  label: string
  /** Chave i18n do rótulo. A sidebar traduz por aqui; ausente = usa `label`. */
  labelKey?: MessageKey
  path: string
  icon: LucideIcon
  /** false/omitido = stub "em breve". */
  ready?: boolean
  /** Só aparece para o admin do cliente (customer_admin). Usado em "Usuários" do portal. */
  adminOnly?: boolean
  /** Key de permissão (view) exigida para exibir o item. Ausente = sempre visível. */
  permission?: string
  /** Basta UMA destas keys. Para o hub que junta telas de módulos diferentes, como
   *  os Cadastros da Logística, em que cada aba pede a key do próprio módulo. */
  anyPermission?: string[]
  /** Item de hub: fica ativo em qualquer subrota de `path` (não só no match exato). */
  matchNested?: boolean
  /** Primeiros segmentos sob `path` que NÃO ativam este item (ex.: rotas de Conjuntos
   *  sob `/logistica/cadastros`). Só vale quando `matchNested`. */
  matchExclude?: string[]
}

/** Subgrupo de 2º nível dentro de uma seção. */
export interface NavSubgroup {
  /** Título em PT (fallback + identidade estável p/ estado aberto/fechado). */
  title: string
  /** Chave i18n do título; ausente = usa `title`. */
  titleKey?: MessageKey
  items: NavItem[]
}

/** Seção da sidebar (cabeçalho sempre visível + itens diretos + subgrupos). */
export interface NavSection {
  /** Título em PT (fallback + identidade estável p/ estado aberto/fechado). */
  title: string
  /** Chave i18n do título; ausente = usa `title`. */
  titleKey?: MessageKey
  items: NavItem[]
  /** Subgrupos de 2º nível, renderizados APÓS os itens diretos. */
  subgroups?: NavSubgroup[]
}

/** Navegação do portal do cliente (empresa cliente da TRANSGRAOS): espelha o
 *  backoffice em 2 seções, mas escopado aos itens do próprio cliente. Sem
 *  "Clientes" (é ele mesmo) nem "Categorias". "Usuários" só p/ o admin. */
export const portalNav: NavSection[] = [
  {
    title: 'Operação',
    titleKey: 'nav.section.operation',
    items: [
      // A Programação entrou no lugar do Mapa em 03/11/2026: o cliente programa as
      // cargas do dia por aqui, e não mais pelo WhatsApp. Primeiro item, para ser a
      // tela inicial de quem só tem a Programação e os Contratos.
      { label: 'Programação', labelKey: 'nav.programacao', path: '/portal/programacao', icon: CalendarClock, ready: true, permission: 'contracts.programar' },
      { label: 'Início', labelKey: 'nav.home', path: '/portal', icon: LayoutDashboard, ready: true, permission: 'dashboard.view' },
      { label: 'Solicitações', labelKey: 'nav.requests', path: '/portal/tickets', icon: ClipboardList, ready: true, permission: 'unloading.view' },
      { label: 'Cargas', labelKey: 'nav.loads', path: '/portal/cargas', icon: Truck, ready: true, permission: 'shipments.view' },
    ],
  },
  {
    title: 'Gestão',
    titleKey: 'nav.section.management',
    items: [
      { label: 'Contratos', labelKey: 'nav.contracts', path: '/portal/contracts', icon: FileText, ready: true, permission: 'contracts.view' },
      { label: 'Usuários', labelKey: 'nav.users', path: '/portal/usuarios', icon: Users, ready: true, adminOnly: true, permission: 'users.view' },
      { label: 'Frota', labelKey: 'nav.fleet', path: '/portal/cadastros/fleet', icon: Truck, ready: true, permission: 'master_data.view' },
      { label: 'Locais', labelKey: 'nav.places', path: '/portal/cadastros/places', icon: MapPin, ready: true, permission: 'master_data.view' },
      { label: 'Pessoas', labelKey: 'nav.people', path: '/portal/cadastros/personnel', icon: Contact, ready: true, permission: 'master_data.view' },
      { label: 'Ativos', labelKey: 'nav.assets', path: '/portal/cadastros/assets', icon: Wheat, ready: true, permission: 'master_data.view' },
    ],
  },
]

/** Achata seções (itens diretos + subgrupos) numa lista de itens (registro de rotas). */
export function flattenNav(sections: NavSection[]): NavItem[] {
  return sections.flatMap((s) => [...s.items, ...(s.subgroups ?? []).flatMap((g) => g.items)])
}
